/**
 * Workflow Control Plane API functions.
 *
 * These call the backend endpoints mounted at /api/v1/workflow/dashboard/.
 * Uses the app's shared `api` helper for consistent auth headers and error handling.
 */

import { api } from "@/helpers/api_helper";
import type {
  AgentsListResponse,
  SessionsListResponse,
  SessionDetailResponse,
  WorkflowStatusResponse,
  CronJobsResponse,
  ConfigsListResponse,
  WorkflowAgent,
  SessionStatus,
} from "./types";

// ── API Base Path ───────────────────────────────────────────────────────

const WF_BASE = "/api/v1/workflow/dashboard";

// ── Agents ──────────────────────────────────────────────────────────────

export const getAgents = async (): Promise<AgentsListResponse> => {
  return api.get<AgentsListResponse>(`${WF_BASE}/agents`, {}, 0, undefined as never) as Promise<AgentsListResponse>;
};

export const createAgent = async (data: Partial<WorkflowAgent>): Promise<WorkflowAgent> => {
  return api.post<WorkflowAgent>(
    `${WF_BASE}/agents`,
    data,
    undefined as never
  ) as Promise<WorkflowAgent>;
};

export const updateAgent = async (
  agentId: string,
  data: Partial<WorkflowAgent>
): Promise<WorkflowAgent> => {
  return api.put<WorkflowAgent>(
    `${WF_BASE}/agents/${agentId}`,
    data,
    undefined as never
  ) as Promise<WorkflowAgent>;
};

// ── Sessions ────────────────────────────────────────────────────────────

export const getSessions = async (params?: {
  status_filter?: string;
  page?: number;
  page_size?: number;
}): Promise<SessionsListResponse> => {
  return api.get<SessionsListResponse>(`${WF_BASE}/sessions`, params ?? {}, 0, undefined as never) as Promise<SessionsListResponse>;
};

export const getSessionDetail = async (sessionId: string): Promise<SessionDetailResponse> => {
  return api.get<SessionDetailResponse>(
    `${WF_BASE}/sessions/${sessionId}`,
    {},
    0,
    undefined as never
  ) as Promise<SessionDetailResponse>;
};

export const updateSession = async (
  sessionId: string,
  data: Record<string, unknown>
): Promise<unknown> => {
  return api.put(`${WF_BASE}/sessions/${sessionId}`, data, undefined as never);
};

export const transitionSession = async (
  sessionId: string,
  status: SessionStatus
): Promise<unknown> => {
  return api.post(`${WF_BASE}/sessions/${sessionId}/transition`, { status }, undefined as never);
};

export const createSession = async (data: {
  title: string;
  description?: string;
  channel?: string;
  priority?: string;
  agent_type?: string;
}): Promise<{ id: string; status: string }> => {
  return api.post(`${WF_BASE}/sessions`, data, undefined as never) as Promise<{
    id: string;
    status: string;
  }>;
};

// ── Messages ────────────────────────────────────────────────────────────

export const sendSessionMessage = async (
  sessionId: string,
  content: string
): Promise<{ id: number; created_at: string }> => {
  return api.post(
    `${WF_BASE}/sessions/${sessionId}/messages`,
    { content, role: "user" },
    undefined as never
  ) as Promise<{ id: number; created_at: string }>;
};

export const sendSystemMessage = async (
  sessionId: string,
  content: string
): Promise<{ id: string; created_at: string }> => {
  return api.post(
    `${WF_BASE}/sessions/${sessionId}/messages`,
    { content, role: "system" },
    undefined as never
  ) as Promise<{ id: string; created_at: string }>;
};

// ── Cron Jobs ───────────────────────────────────────────────────────────

export const getCronJobs = async (): Promise<CronJobsResponse> => {
  return api.get<CronJobsResponse>(`${WF_BASE}/cron`, {}, 0, undefined as never) as Promise<CronJobsResponse>;
};

// ── Status ──────────────────────────────────────────────────────────────

export const getWorkflowStatus = async (): Promise<WorkflowStatusResponse> => {
  return api.get<WorkflowStatusResponse>(`${WF_BASE}/status`, {}, 0, undefined as never) as Promise<WorkflowStatusResponse>;
};

// ── Commands ────────────────────────────────────────────────────────────

export const sendCommand = async (
  command: "restart" | "config-reload",
  payload?: Record<string, unknown>
): Promise<unknown> => {
  return api.post(`${WF_BASE}/commands`, { command, payload }, undefined as never);
};

export const cancelCommand = async (commandId: string): Promise<unknown> => {
  return api.post(`${WF_BASE}/commands/${commandId}/cancel`, {}, undefined as never);
};

// ── Config ──────────────────────────────────────────────────────────────

export const getConfigs = async (namespace?: string): Promise<ConfigsListResponse> => {
  const params = namespace ? { namespace } : {};
  return api.get<ConfigsListResponse>(`${WF_BASE}/config`, params, 0, undefined as never) as Promise<ConfigsListResponse>;
};

export const upsertConfig = async (body: {
  namespace: string;
  key: string;
  value: unknown;
  encrypted?: boolean;
}): Promise<unknown> => {
  return api.put(`${WF_BASE}/config`, body, undefined as never);
};

export const deleteConfig = async (configId: string): Promise<{ ok: boolean }> => {
  return api.delete<{ ok: boolean }>(`${WF_BASE}/config/${configId}`, {}, undefined as never) as Promise<{ ok: boolean }>;
};
